/**
 * Series-aware prompt context (Phase 1B #168 / #223).
 * Extracted from NewsletterAIService.js as part of #229 decomposition.
 *
 * Public exports:
 *   - buildPromptContext({ seriesId }) — resolves the active series +
 *     advisor profile and returns the placeholders + audience cues a
 *     niche-aware prompt template needs. Throws if seriesId is missing
 *     or the series doesn't exist.
 *   - NICHE_FRAMING — the 5-niche enum's audience framing strings.
 *     Exported for testability and so consumers can introspect supported niches.
 *
 * Closes #128, #131 (advisor identity bridge), #130 (cadence/tone consumption).
 */

import publicationSeriesService from '../PublicationSeriesService.js';
import profileService from '../ProfileService.js';

export const NICHE_FRAMING = {
  retirees: 'Audience: retirees focused on income, RMDs, estate planning.',
  hnw: 'Audience: high-net-worth — tax minimization, estate planning, charitable giving.',
  tech_employees: 'Audience: tech employees — RSUs, ISOs, IPO planning.',
  medical_professionals:
    'Audience: medical professionals — student loans, disability insurance, self-employment tax.',
  busy_parents: 'Audience: busy parents — college savings, life insurance, first home.',
};

export async function buildPromptContext({ seriesId } = {}) {
  if (!seriesId) {
    throw new Error('buildPromptContext: seriesId is required');
  }
  const series = await publicationSeriesService.getSeries(seriesId);
  if (!series) {
    throw new Error(`buildPromptContext: series ${seriesId} not found`);
  }

  // Always attempt the profile lookup — Phase 1A backfill ensures userId is
  // populated. Pass undefined through to the service for the rare legacy case.
  const profile = await profileService.getProfileByUserId(series.userId);

  // Audience cues — pulled from audienceProfile + niche + audienceDescription.
  // audienceDescription overrides niche framing when present.
  const cues = [];
  if (series.audienceDescription) {
    cues.push(`Audience: ${series.audienceDescription}`);
  } else if (series.niche && NICHE_FRAMING[series.niche]) {
    cues.push(NICHE_FRAMING[series.niche]);
  }
  const ap = series.audienceProfile;
  if (ap) {
    if (ap.lifeStage) cues.push(`Life stage: ${ap.lifeStage}`);
    if (ap.primaryConcern) cues.push(`Primary concern: ${ap.primaryConcern}`);
    if (ap.technicalDepth) cues.push(`Technical depth: ${ap.technicalDepth}`);
    if (Array.isArray(ap.planningFocus) && ap.planningFocus.length > 0) {
      cues.push(`Planning focus: ${ap.planningFocus.join(', ')}`);
    }
  }

  // Placeholder resolution — UserProfile is the default source; the series'
  // communicationStyleOverride wins when populated (per spec § 2.2 #131).
  const override = series.communicationStyleOverride || {};
  const fullName = profile
    ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
    : '';
  const placeholders = {
    advisor_name: override.advisorName || fullName,
    company_name: override.companyName || (profile?.company ?? ''),
    advisor_title: override.advisorTitle || (profile?.title ?? ''),
    tone: override.tone || '',
  };

  return {
    audienceCues: cues,
    placeholders,
    cadence: series.cadence || null,
    niche: series.niche || null,
  };
}
